import axios from 'axios';
import { config } from '../config';
import { createAsyncThunk } from '@reduxjs/toolkit';

// Helper to get headers - includes Authorization token if user is logged in
const getHeaders = () => {
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  
  // Add Authorization token if user is logged in (stored in localStorage)
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface State {
  id: string;
  name: string;
  code: string;
}

export interface FacilityState {
  id: string;
  name: string;
  code: string;
}

export interface City {
  id: string;
  name: string;
  stateCode: string;
}

export interface Facility {
  id: string;
  name: string;
  code: string;
  stateCode: string;
  cityId: string;
}

export interface LossType {
  id: string;
  name: string;
  code: string;
}

export interface InventoryItem {
  id: number;
  tpp_claim_id: number;
  item_description: string;
  damage_description?: string | null;
  date_of_purchase: string | null;
  price: string | number | null;
  has_receipt: boolean;
  has_damage_evidence: boolean;
  attachments?: Array<{
    id: number;
    type: string;
    original_filename: string;
    token: string;
    path: string;
  }>;
}

export const getFacilityStates = async (): Promise<FacilityState[]> => {
  try {
    const response = await axios.get(`${config.API_URL}/facilities/states`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching facility states:', error);
    throw error;
  }
};

export const fetchCitiesByState = createAsyncThunk(
  'cities/fetchByState',
  async (stateCode: string) => {
    try {
      const response = await axios.get(`${config.API_URL}/facilities/cities?state=${stateCode}`,
        {
          headers: getHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
  }
);

export const searchFacilities = createAsyncThunk(
  'facilities/search',
  async (searchParams: { state: string; city: string }) => {
    try {
      const response = await axios.post(`${config.API_URL}/facilities/search`,
        searchParams,
        {
          headers: getHeaders(),
        }
      );
      
      return response.data;

    } catch (error) {
      console.error('Error searching facilities:', error);
      throw error;
    }
  }
);

export const getStates = async (): Promise<State[]> => {
  try {
    const response = await axios.get(`${config.API_URL}/states`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching states:', error);
    throw error;
  }
};

export const getLossTypes = async (): Promise<LossType[]> => {
  try {
    const response = await axios.get(`${config.API_URL}/loss-types`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching loss types:', error);
    throw error;
  }
};

const DRAFT_ONLY_FIELDS = [
  'facility_state', 'facility_city', 'facility_id', 'facility_unit',
  'reporter_first_name', 'reporter_last_name', 'reporter_phone',
  'owner_email', 'owner_address_1', 'owner_address_2', 'owner_city', 'owner_state', 'owner_zip_code',
  'status',
];

/** Payload for creating or updating a draft from the Get Started step (shared POST/PUT shape). */
export const buildDraftClaimPayload = (formData: any): Record<string, any> => {
  const payload: Record<string, any> = {};
  for (const key of DRAFT_ONLY_FIELDS) {
    if (formData[key] !== undefined && formData[key] !== null) {
      payload[key] = formData[key];
    }
  }
  if (payload.reporter_phone) {
    payload.reporter_phone = String(payload.reporter_phone).replace(/\D/g, '');
  }
  payload.status = 'DRAFT';
  return payload;
};

/** Create a draft claim with only Get Started fields (no incident/damage fields required) */
export const createClaim = async (formData: any): Promise<any> => {
  try {
    const payload = buildDraftClaimPayload(formData);
    const response = await axios.post(`${config.API_URL}/claims`, payload, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error creating claim:', error);
    throw error;
  }
};

export const updateClaim = async (claimId: number, payload: Record<string, any>): Promise<any> => {
  try {
    const body = { ...payload };
    if (body.reporter_phone) {
      body.reporter_phone = body.reporter_phone.replace(/\D/g, '');
    }
    if (body.damage_estimate) {
      body.damage_estimate = body.damage_estimate.replace(/,/g, '');
    }
    const response = await axios.put(`${config.API_URL}/claims/${claimId}`, body, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating claim:', error);
    throw error;
  }
};

export const getClaim = async (claimId: number): Promise<any> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/${claimId}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching claim:', error);
    throw error;
  }
};

export const getClaimByToken = async (token: string): Promise<any> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/token/${encodeURIComponent(token)}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching claim by token:', error);
    throw error;
  }
};

export const getClaimByReference = async (reference: string): Promise<any> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/reference/${encodeURIComponent(reference)}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching claim by reference:', error);
    throw error;
  }
};

/** Claim-level attachment rows used for the incident law enforcement step (type `law_enforcement_report`). */
export interface LawEnforcementAttachmentMeta {
  id: number;
  type: string;
  original_filename: string;
  token: string;
}

export function filterLawEnforcementAttachments(attachments: unknown): LawEnforcementAttachmentMeta[] {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .filter(
      (a: any) =>
        a &&
        typeof a === 'object' &&
        String(a.type) === 'law_enforcement_report' &&
        a.token != null &&
        String(a.token) !== ''
    )
    .map((a: any) => ({
      id: Number(a.id),
      type: String(a.type),
      original_filename: String(a.original_filename ?? ''),
      token: String(a.token ?? ''),
    }))
    .filter((a) => Number.isFinite(a.id) && a.token !== '');
}

export function getClaimAttachmentPreviewUrl(
  claimId: number,
  attachmentId: number,
  token: string
): string {
  return `${config.API_URL}/claims/${claimId}/attachments/${attachmentId}/preview?token=${encodeURIComponent(token)}`;
}

export const deleteAllLawEnforcementAttachments = async (
  claimId: number
): Promise<{ deleted: number }> => {
  const response = await axios.delete(
    `${config.API_URL}/claims/${claimId}/attachments/law-enforcement-reports`,
    { headers: getHeaders() }
  );
  return response.data;
};

const DEFAULT_MAX_ATTACHMENT_FILE_KB = 40960;

function readMaxAttachmentFileKbFromEnv(): number {
  const raw = process.env.REACT_APP_MAX_ATTACHMENT_FILE_KB;
  if (raw === undefined || raw === '') {
    return DEFAULT_MAX_ATTACHMENT_FILE_KB;
  }
  const parsed = Number.parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_ATTACHMENT_FILE_KB;
}

/** Kilobytes; from `REACT_APP_MAX_ATTACHMENT_FILE_KB` or default 40960 (matches Laravel `max:40960`). */
export const MAX_CLAIM_ATTACHMENT_FILE_KB = readMaxAttachmentFileKbFromEnv();

/** Build-time fallback before `config.json` loads; uploads use `config.MAX_CLAIM_ATTACHMENT_FILE_BYTES` at runtime. */
export const MAX_CLAIM_ATTACHMENT_FILE_BYTES = MAX_CLAIM_ATTACHMENT_FILE_KB * 1024;

function maxAttachmentMbLabel(maxBytes: number): string {
  const mib = maxBytes / (1024 * 1024);
  return Number.isInteger(mib) ? String(mib) : mib.toFixed(1);
}

/** Same units as max label: mebibytes, shown as "MB". */
function fileSizeMbLabel(sizeBytes: number): string {
  const mib = sizeBytes / (1024 * 1024);
  return Number.isInteger(mib) ? String(mib) : mib.toFixed(1);
}

export const uploadClaimAttachments = async (claimId: number, files: File[], type: string): Promise<any> => {
  try {
    const maxBytes = config.MAX_CLAIM_ATTACHMENT_FILE_BYTES;
    const maxMbLabel = maxAttachmentMbLabel(maxBytes);
    for (const file of files) {
      if (file.size > maxBytes) {
        const actualMb = fileSizeMbLabel(file.size);
        throw new Error(
          `File "${file.name}" is ${actualMb} MB (${file.size.toLocaleString()} bytes). The maximum is ${maxMbLabel} MB. Please choose a smaller file.`
        );
      }
    }

    const headers = getHeaders();
    // Remove Content-Type for FormData (browser will set it with boundary)
    delete headers['Content-Type'];
    
    // Upload each file individually
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await axios.post(`${config.API_URL}/claims/${claimId}/attachments`, formData, {
        headers,
      });
      return response.data;
    });
    
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Error uploading attachments:', error);
    throw error;
  }
};

export const sendClaimConfirmation = async (claimId: number): Promise<void> => {
  try {
    await axios.post(`${config.API_URL}/claims/${claimId}/send-confirmation`, {}, {
      headers: getHeaders(),
    });
  } catch (error) {
    console.error('Error sending claim confirmation email:', error);
    throw error;
  }
}; 

export const getInventoryItems = async (claimId: number): Promise<InventoryItem[]> => {
  const response = await axios.get(`${config.API_URL}/claims/${claimId}/inventory-items`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const createInventoryItem = async (claimId: number, payload: Partial<InventoryItem>): Promise<InventoryItem> => {
  const response = await axios.post(`${config.API_URL}/claims/${claimId}/inventory-items`, payload, {
    headers: getHeaders(),
  });
  return response.data;
};

export const deleteInventoryItem = async (claimId: number, itemId: number): Promise<void> => {
  await axios.delete(`${config.API_URL}/claims/${claimId}/inventory-items/${itemId}`, {
    headers: getHeaders(),
  });
};

export const updateInventoryItem = async (
  claimId: number,
  itemId: number,
  payload: Partial<InventoryItem>
): Promise<InventoryItem> => {
  const response = await axios.put(
    `${config.API_URL}/claims/${claimId}/inventory-items/${itemId}`,
    payload,
    { headers: getHeaders() }
  );
  return response.data;
};

export const uploadInventoryAttachments = async (
  claimId: number,
  inventoryItemId: number,
  files: File[],
  type: 'inventory_receipt' | 'inventory_evidence'
): Promise<any> => {
  if (type === 'inventory_evidence' && files.length > 2) {
    throw new Error('Error - A maximum of 2 files per damaged/stolen item can be uploaded.');
  }

  const headers = getHeaders();
  delete headers['Content-Type'];

  const uploadPromises = files.map(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('inventory_item_id', String(inventoryItemId));

    const response = await axios.post(`${config.API_URL}/claims/${claimId}/attachments`, formData, {
      headers,
    });
    return response.data;
  });

  return Promise.all(uploadPromises);
};

export const deleteInventoryAttachment = async (
  claimId: number,
  attachmentId: number
): Promise<void> => {
  await axios.delete(
    `${config.API_URL}/claims/${claimId}/inventory-attachments/${attachmentId}`,
    { headers: getHeaders() }
  );
};

export interface ClaimStatusActivityItem {
  id: number;
  note_type: string;
  note: string;
  sender_label: string;
  is_from_tenant: boolean;
  created_at: string;
}

export interface ClaimStatusResponse {
  claim: {
    id: number;
    claim_number: string;
    token: string;
    status: string;
    status_label: string;
    status_description: string;
    created_at: string;
    created_by: string;
    facility_unit: string | null;
    coverage_limit: string | number | null;
    claim_total: string | number | null;
    peril: string | null;
    agent_name: string;
    resume_url: string;
  };
  activity: ClaimStatusActivityItem[];
  todo_list: string[];
  /** Raw HTML from portal peril settings (preferred over todo_list when present). */
  todo_html?: string | null;
}

const claimStatusInflight = new Map<string, Promise<ClaimStatusResponse>>();

export const getClaimStatusByToken = async (token: string): Promise<ClaimStatusResponse> => {
  const key = token.trim().toUpperCase();
  const existing = claimStatusInflight.get(key);
  if (existing) {
    return existing;
  }

  const request = axios
    .get(`${config.API_URL}/claims/token/${encodeURIComponent(token)}/status`, {
      headers: getHeaders(),
    })
    .then((response) => response.data as ClaimStatusResponse)
    .finally(() => {
      claimStatusInflight.delete(key);
    });

  claimStatusInflight.set(key, request);
  return request;
};

export const sendClaimMessage = async (token: string, note: string): Promise<ClaimStatusActivityItem> => {
  const response = await axios.post(
    `${config.API_URL}/claims/token/${encodeURIComponent(token)}/messages`,
    { note },
    { headers: getHeaders() }
  );
  return response.data;
};

export const resubmitIncompleteClaim = async (token: string): Promise<{ claim: ClaimStatusResponse['claim'] }> => {
  const response = await axios.post(
    `${config.API_URL}/claims/token/${encodeURIComponent(token)}/resubmit`,
    {},
    { headers: getHeaders() }
  );
  return response.data;
};